import {useCallback} from 'react'
import {useI18n} from '@/i18n'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {useRemoveServiceAccountRole} from '@/features/service-accounts/hooks/useServiceAccountRoles'

interface RemoveRoleDialogProps {
    serviceAccountId: string
    serviceAccountName: string
    roleId: string | null
    roleName: string
    open: boolean
    onOpenChange: (next: boolean) => void
}

export function RemoveRoleDialog({
    serviceAccountId,
    serviceAccountName,
    roleId,
    roleName,
    open,
    onOpenChange,
}: RemoveRoleDialogProps) {
    const i18n = useI18n()
    const removeMutation = useRemoveServiceAccountRole(serviceAccountId)

    const handleConfirm = useCallback(() => {
        if (!roleId) return
        removeMutation.mutate(roleId, {
            onSuccess: () => onOpenChange(false),
        })
    }, [roleId, removeMutation, onOpenChange])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle data-testid="remove-role-dialog-title">
                        {i18n.serviceAccounts.removeRoleTitle}
                    </DialogTitle>
                    <DialogDescription data-testid="remove-role-dialog-body">
                        {i18n.serviceAccounts.removeRoleBody({role: roleName, serviceAccount: serviceAccountName})}
                    </DialogDescription>
                </DialogHeader>
                <SaveCancelFooter
                    testIdPrefix="remove-role"
                    onCancel={() => onOpenChange(false)}
                    saving={removeMutation.isPending}
                    saveLabel={i18n.serviceAccounts.removeRoleConfirm as string}
                    savingLabel={i18n.serviceAccounts.removeRoleConfirm as string}
                    destructive
                    saveType="button"
                    onSave={handleConfirm}
                />
            </DialogContent>
        </Dialog>
    )
}
